/**
 * QuickActionsWidget
 *
 * Dashboard widget that displays action buttons for Contribute, Give, and Share.
 */
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Button, Grid, Paper, Typography } from '@mui/material'
import { Add, VolunteerActivism, Share } from '@mui/icons-material'

// Calculate grid columns based on number of actions
function getGridColumns(count) {
  if (count <= 1) return 12
  if (count === 2) return 6
  return 4
}

export function QuickActionsWidget({ fundId, showContribute = true, showGive = true, showShare = true }) {
  const navigate = useNavigate()
  const params = useParams()
  const activeFundId = fundId || params.fundId

  const handleContribute = () => {
    navigate(`/funds/${activeFundId}/contribute`)
  }

  const handleGive = () => {
    navigate(`/funds/${activeFundId}/grant`)
  }

  const handleShare = () => {
    navigate(`/funds/${activeFundId}/share-funds-with-others`)
  }

  const actions = [
    {
      key: 'contribute',
      show: showContribute,
      label: 'Add Funds',
      description: 'Contribute to your giving fund',
      icon: <Add />,
      onClick: handleContribute,
      color: 'primary',
    },
    {
      key: 'give',
      show: showGive,
      label: 'Give',
      description: 'Make a gift to a charity',
      icon: <VolunteerActivism />,
      onClick: handleGive,
      color: 'secondary',
    },
    {
      key: 'share',
      show: showShare,
      label: 'Share Funds',
      description: 'Share funds with others',
      icon: <Share />,
      onClick: handleShare,
      color: 'info',
    },
  ].filter((action) => action.show)

  if (actions.length === 0) {
    return null
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Quick Actions
      </Typography>
      <Grid container spacing={2}>
        {actions.map((action) => (
          <Grid item xs={12} sm={getGridColumns(actions.length)} key={action.key}>
            <Paper
              sx={{
                p: 2,
                cursor: 'pointer',
                transition: 'box-shadow 0.2s, transform 0.2s',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                },
              }}
              onClick={action.onClick}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: `${action.color}.light`,
                    color: `${action.color}.main`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {action.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {action.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}
